import { useState, useEffect } from 'react';
import {
    Box, TextField, Grid
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import API_URL from '../../config/api';
import { FaEye, FaTrash } from 'react-icons/fa';
import DownloadIcon from '../../assets/download.svg';
import { isUserAdmin } from '../../utils/auth';
import Footer from '../../components/Footer/Footer';
import ShpIcon from '../../assets/shapefile_dark.svg';
import './DownloadShapefile.css';

const ListShapefile = () => {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrigens, setSelectedOrigens] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const token = JSON.parse(atob(localStorage.getItem('token') || '""'));

    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                const response = await fetch(`${API_URL}/api/Layers`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setDocuments(data);
                } else if (response.status === 401) {
                    toast.error("Sessão expirada, faça login novamente.");
                    localStorage.clear();
                    navigate("/");
                } else if (response.status === 403) {
                    toast.warn("Acesso negado.");
                } else {
                    throw new Error('Erro ao carregar documentos.');
                }
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDocuments();
    }, [navigate, token]);

    const handleDownload = async (filePath, nameFileRAR) => {
        try {
            const url = `${API_URL}/api/Layers/download?filePath=${encodeURIComponent(filePath)}&nameFileRAR=${encodeURIComponent(nameFileRAR)}`;
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("Falha no download.");

            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = nameFileRAR.endsWith('.zip') || nameFileRAR.endsWith('.rar') ? nameFileRAR : `${nameFileRAR}.zip`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            toast.error("Erro ao baixar o arquivo.");
        }
    };

    const handleDelete = async (id, nome) => {
        const confirmDelete = window.confirm(`Deseja realmente excluir o documento "${nome}"?`);
        if (!confirmDelete) return;

        try {
            const response = await fetch(`${API_URL}/api/Layers/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success("Documento deletado com sucesso.");
                setDocuments(prev => prev.filter(doc => doc.id !== id));
            } else {
                toast.error("Erro ao deletar o documento.");
            }
        } catch (err) {
            toast.error("Erro de conexão ao tentar deletar.");
        }
    };

    const filteredDocuments = documents
        .filter(doc => selectedOrigens.length === 0 || selectedOrigens.includes(doc.servico))
        .filter(doc => doc.servico.toLowerCase().includes(searchTerm.toLowerCase()))
        .filter(doc => {
            const date = new Date(doc.dataUpload);
            if (startDate && date <= startDate.toDate()) return false;
            if (endDate && date >= endDate.toDate()) return false;
            return true;
        });

    if (loading) return <p>Carregando documentos...</p>;
    if (error) return <p>Erro: {error}</p>;

    return (
        <>
            <div className="layout-wrapper">
                <Navbar />
                <div className="container">
                    <h2 className="title shapefile-title">
                        <span className="icon-wrapper">
                            <img src={ShpIcon} alt="Shapefile Icon" width={32} height={40} />
                        </span>
                        Lista de Shapefiles
                    </h2>

                    {/* Filtros */}
                    <Box sx={{ mb: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Buscar por nome"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data início"
                                        value={startDate}
                                        onChange={setStartDate}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data fim"
                                        value={endDate}
                                        onChange={setEndDate}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Tabela */}
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nome Layer</th>
                                <th>Grupo</th>
                                <th>Data Upload</th>
                                <th className="th-center">Visualizar</th>
                                <th className="th-center">Download</th>
                                {isUserAdmin() && <th className="th-center">Excluir</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDocuments.map((doc) => (
                                <tr key={doc.id}>
                                    <td>{doc.servico}</td>
                                    <td>{doc.grupo}</td>
                                    <td>{new Date(doc.dataUpload).toLocaleString()}</td>
                                    <td className="td-center">
                                        <a
                                            onClick={() => {
                                                if (doc.arquivos && doc.arquivos.length > 0) {
                                                    alert("Arquivos:\n" + doc.arquivos.join('\n'));
                                                } else {
                                                    alert("Nenhum arquivo encontrado.");
                                                }
                                            }}
                                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            className="button-icon"
                                            title="Visualizar Arquivos Shapefile"
                                        >
                                            <FaEye size={20} color="#0000FF" />
                                        </a>
                                    </td>
                                    <td className="td-center">
                                        <button
                                            className="button-icon"
                                            onClick={() => handleDownload(doc.url, doc.nome)}
                                            title="Download"
                                        >
                                            <img src={DownloadIcon} alt="Download" className="icon-download" />
                                        </button>
                                    </td>
                                    {isUserAdmin() && (
                                        <td className="td-center">
                                            <button
                                                className="button-icon"
                                                onClick={() => handleDelete(doc.id, doc.nome)}
                                                title="Excluir Documento"
                                            >
                                                <FaTrash size={18} style={{ color: '#dc2626' }} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <ToastContainer />
                </div>
                <Footer />
            </div>
        </>
    );
};

export default ListShapefile;
